/**
 * LLM Prompts API Client with React Query Hooks
 *
 * Provides type-safe API client for LLM Prompt Management System using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 */

import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPut } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

// Prompt Template Schema
const PromptTemplateSchema = z.object({
  languageCode: z.string().regex(/^[a-z]{2}$/),
  template: z.string().min(1),
});

// Model Config Schema - Flexible with required 'model' key
const ModelConfigSchema = z
  .object({
    model: z.string().min(1, 'Model is required'),
  })
  .catchall(z.any()); // Allow any other keys with any values

// Prompt Response Schema
const PromptResponseSchema = z.object({
  id: z.number(),
  promptKey: z.string().max(100),
  promptGroup: z.string(),
  description: z.string().nullable(),
  promptTemplates: z.array(PromptTemplateSchema).min(1),
  modelConfig: ModelConfigSchema,
  provider: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Prompt List Response Schema
const PromptListResponseSchema = z.array(PromptResponseSchema);

// Export TypeScript types from Zod schemas
export type PromptTemplate = z.infer<typeof PromptTemplateSchema>;
export type ModelConfig = z.infer<typeof ModelConfigSchema>;
export type PromptResponse = z.infer<typeof PromptResponseSchema>;
export type PromptListResponse = z.infer<typeof PromptListResponseSchema>;

// Prompt Update Type (inlined to avoid unused variable warning)
export type PromptUpdate = {
  promptTemplates?: PromptTemplate[];
  modelConfig?: ModelConfig;
  description?: string | null;
  provider?: string;
};

// Prompt filters for list endpoint
export interface PromptFilters {
  promptKey?: string;
}

// ============================================================================
// STEP 2: Define Fetcher Functions
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// Prompt Management Fetchers
const fetchPrompts = async (filters?: PromptFilters): Promise<PromptListResponse> => {
  try {
    const params = filters
      ? transformCamelToSnake(filters as unknown as Record<string, unknown>)
      : undefined;
    const data = await apiGet<unknown[]>('/api/admin/llm-prompts', params);
    return PromptListResponseSchema.parse(
      data.map((item) => transformSnakeToCamel(item as Record<string, unknown>))
    );
  } catch (error) {
    logger.error('Error fetching prompts:', error);
    throw error;
  }
};

const fetchPromptByKey = async (promptKey: string): Promise<PromptResponse> => {
  try {
    const data = await apiGet<unknown>(`/api/admin/llm-prompts/${promptKey}`);
    return PromptResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error fetching prompt by key:', error);
    throw error;
  }
};

const updatePrompt = async (promptKey: string, update: PromptUpdate): Promise<PromptResponse> => {
  try {
    const payload = transformCamelToSnake(update as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(`/api/admin/llm-prompts/${promptKey}`, payload);
    return PromptResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating prompt:', error);
    throw error;
  }
};

// ============================================================================
// STEP 3: Define React Query Hooks
// ============================================================================

/**
 * Fetch all prompts with optional filters
 */
export const usePrompts = (
  filters?: PromptFilters,
  options?: Omit<UseQueryOptions<PromptListResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['llm-prompts', filters],
    queryFn: () => fetchPrompts(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Fetch single prompt by prompt_key
 */
export const usePrompt = (promptKey: string | undefined) => {
  return useQuery({
    queryKey: ['llm-prompt', promptKey],
    queryFn: () => fetchPromptByKey(promptKey!),
    enabled: !!promptKey,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update existing prompt
 */
export const useUpdatePrompt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ promptKey, update }: { promptKey: string; update: PromptUpdate }) =>
      updatePrompt(promptKey, update),
    onSuccess: (updatedPrompt) => {
      // Optimistic update - update the specific prompt in cache
      queryClient.setQueryData(['llm-prompt', updatedPrompt.promptKey], updatedPrompt);

      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['llm-prompts'] });
    },
  });
};
